import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, ArrowLeft, Loader2, Paperclip, Trash2, ChevronLeft, ChevronRight, History, Tag } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import api from './axiosConfig';
import { resolveIcon } from './iconResolver';
import FieldInput from './CustomFieldInput';
import AttachmentsModal from './AttachmentsModal';
import SmartPrintButton from './PrintButton';
import AuditHistoryModal from './AuditHistoryModal';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

// حقل بسيط (نص/رقم/تاريخ) يقدر يتحرر جوه شريط الهيدر نفسه بدل ما يتكرر في
// الفورمة تاني. الأنواع المعقدة (select/link/boolean/table/textarea)
// بتفضل جوه الفورمة بس، محتاجة ودجت مخصص مش هيتناسب مع شريط ضيق
const HEADER_EDITABLE_TYPES = ['text', 'number', 'date', 'datetime'];

const CustomModelForm = ({ slug }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [meta, setMeta] = useState(null);
  const [data, setData] = useState({});
  // 🎯 on_change بيبعت request async ممكن ياخد وقت - لو المستخدم عدّل حاجة
  // تانية (زي اختيار item من اللوكاب) وهو لسه ماشي، رد الـ request القديم
  // كان بيجي بعدين ويكتب فوق التعديل الجديد (بيفلاش القيمة المختارة وترجع
  // تختفي). الحل: عداد جيل (generation) بيزيد مع أي تعديل حقيقي على data،
  // ورد on_change بيتجاهل لو الجيل اتغير من وقت ما الـ request اتبعت
  const dataGenRef = useRef(0);
  const updateData = useCallback((updater) => {
    dataGenRef.current += 1;
    setData(updater);
  }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('General');
  const [showAttachments, setShowAttachments] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showStatusHistory, setShowStatusHistory] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

  const { canDelete } = usePermissions(meta?.module_key);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const metaRes = await api.get(`/custom-models/${slug}`);
      setMeta(metaRes.data);
      if (!isNew) {
        const recRes = await api.get(`/custom-records/${id}`);
        setData(recRes.data.data || {});
      }
    } catch (err) {
      toast.error(t('customApps.loadFailed', 'Failed to load this application'));
    } finally {
      setLoading(false);
    }
  }, [slug, id, isNew, t]);

  useEffect(() => { load(); }, [load]);

  // 🎯 عدّاد المرفقات على الأيقونة - نفس endpoint المستخدم فعليًا في
  // WorkOrderDetails.js (GET /attachments/count/{ownertable}/{ownerid})
  // وهو عام أصلاً (بيفلتر بس على ownertable/ownerid)، فمفيش داعي لأي تعديل
  // بالباك إند عشان يشتغل مع custom_<slug>
  useEffect(() => {
    if (isNew || !meta?.accepts_attachments) { setAttachmentCount(0); return; }
    api.get(`/attachments/count/custom_${slug}/${id}`)
      .then((res) => setAttachmentCount(res.data.count || 0))
      .catch(() => setAttachmentCount(0));
  }, [isNew, meta?.accepts_attachments, slug, id, showAttachments]);

  // 🎯 نافيجيشن جري/تالي زي شاشة الورك اوردر - endpoint عام جديد
  // (/custom-records/{slug}/{id}/navigation) بيرتب بنفس ترتيب الليست
  useEffect(() => {
    if (isNew) return;
    api.get(`/custom-records/${slug}/${id}/navigation`)
      .then((res) => setNav(res.data))
      .catch(() => setNav({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 }));
  }, [isNew, slug, id]);

  const goToRecord = (recId) => {
    if (recId) navigate(`/custom/${slug}/${recId}`);
  };

  // 🎯 رسائل الـ Action Scripts (before_save/after_save) بتاعت الموديل - كل
  // سكربت بيقدر ينادي action.log/action.toast/action.prompt، والباك إند
  // بيرجّعهم في رد الحفظ (messages) عشان نعرضهم هنا فعليًا بدل ما يضيعوا.
  // "log" مجرد تتبّع في الكونسول (اللوج الحقيقي راح للسيرفر أصلاً)، "toast"
  // نفس نظام التوست العادي في الشاشة، و"prompt" مودال لازم اليوزر يقفله
  // بنفسه (SweetAlert2) - مش رسالة عابرة زي التوست
  const handleScriptMessages = async (messages) => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    for (const m of messages) {
      if (m.type === 'toast') {
        const level = m.level || 'info';
        if (level === 'success') toast.success(m.text);
        else if (level === 'error') toast.error(m.text);
        else toast(m.text);
      } else if (m.type === 'prompt') {
        await Swal.fire({ ...swalTheme(), title: m.text });
      } else if (m.type === 'log') {
        console.log('[Custom Script]', m.text);
      }
    }
  };

  // 🎯 الحدث اللحظي on_change - بيتنادى لما حقل عليه سكربت on_change فعال
  // يخسر الفوكس (onBlur بيبوبل من أي عنصر جواه، فمش محتاجين نعدل
  // CustomFieldInput.js نفسه). بيبعت نسخة "data" الحالية كاملة، ويستقبل
  // نسخة معدّلة + رسائل بالظبط زي before_save، لكن مفيش أي blocking - لو
  // السكربت فشل السيرفر بيسجله بس ومبيرجعش حاجة
  const handleFieldBlur = async (field) => {
    if (!field?.has_on_change) return;
    const genAtRequest = dataGenRef.current;
    try {
      const res = await api.post('/custom-records/on-change', {
        model_slug: slug,
        field_name: field.field_name,
        data,
      });
      // لو المستخدم عدّل حاجة تانية (زي اختيار item من اللوكاب) وهو الـ
      // request ده لسه ماشي، الرد ده بقى قديم - نتجاهله عشان ميمسحش تعديل
      // أحدث منه
      if (dataGenRef.current !== genAtRequest) return;
      updateData(res.data.data || data);
      await handleScriptMessages(res.data.messages);
    } catch (err) {
      // best-effort فعلاً - لو الطلب فشل (نت مثلاً) منعملش حاجة تعطل الكتابة
    }
  };

  const doSave = useCallback(async () => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post('/custom-records', { model_slug: slug, data });
        toast.success(t('common.saved', 'Saved'));
        await handleScriptMessages(res.data.messages);
        navigate(`/custom/${slug}/${res.data.id}`);
      } else {
        const res = await api.put(`/custom-records/${id}`, { data });
        toast.success(t('common.saved', 'Saved'));
        await handleScriptMessages(res.data.messages);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.saveFailed', 'Save failed'));
    } finally {
      setSaving(false);
    }
  }, [isNew, slug, id, data, navigate, t]);

  const handleSubmit = (e) => {
    e.preventDefault();
    doSave();
  };

  const doDelete = useCallback(async () => {
    if (isNew) return;
    const result = await Swal.fire({
      ...swalTheme(),
      title: t('common.areYouSure', 'Are you sure?'),
      text: t('customApps.deleteRecordConfirm', 'This record will be moved to the Recycle Bin and can be restored later.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.yesDelete', 'Yes, delete it!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      reverseButtons: isRTL,
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/custom-records/${id}`);
      toast.success(t('common.deleted', 'Deleted'));
      navigate(`/custom/${slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.deleteFailed', 'Delete failed'));
    }
  }, [isNew, id, slug, navigate, t, isRTL]);

  // 🎯 زراير السايدبار العامة (Save/Delete) بتبعت CustomEvent مش بتنادي
  // الشاشة مباشرة - لازم نسمعها هنا بالظبط زي كل شاشات التفاصيل التانية
  // في التطبيق (WorkOrderDetails.js وغيره) عشان الأزرار "تشتغل" فعلاً
  useEffect(() => {
    window.addEventListener('GLOBAL_SAVE_HANDLER', doSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', doSave);
  }, [doSave]);

  useEffect(() => {
    window.addEventListener('GLOBAL_DELETE_HANDLER', doDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', doDelete);
  }, [doDelete]);

  // 🎯 تغيير الحالة - بنستخدم نفس المحرك الحقيقي المستخدم فعليًا في شاشات
  // الأصول وأوامر الشغل (StatusModal + POST /status/change + StatusHistory)
  // مش لوكاب دومين خام بيسيف على طول - ده بيدّي مذكرة (memo) وتسجيل
  // تاريخي حقيقي لكل تغيير، مش مجرد قيمة بتتبدل جوه data
  const statusEnabled = Boolean(meta?.layout?.status?.enabled);
  const statusAppName = `CUSTOM_${slug.toUpperCase()}`;
  const currentStatus = data.__status;

  useEffect(() => {
    if (!statusEnabled) return;
    const openStatusModal = () => setShowStatusModal(true);
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, [statusEnabled]);

  useEffect(() => {
    if (!statusEnabled) return;
    const openHistory = () => setShowStatusHistory(true);
    window.addEventListener('OPEN_STATUS_HISTORY', openHistory);
    return () => window.removeEventListener('OPEN_STATUS_HISTORY', openHistory);
  }, [statusEnabled]);

  // 🎯 زرار "Audit Log" العام في الـ AppSidebar (More Actions) بيبعت
  // GLOBAL_LOG_HANDLER، مش أي حدث تاني - ده اللي كان ناقص وخلى زرار
  // الأوديت "مش شغال"
  useEffect(() => {
    const openAudit = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openAudit);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openAudit);
  }, []);

  // نفس التجميع تاب > سكشن اللي شغال في الاستوديو بالظبط - الفورم اللي
  // بيتولد هنا لازم يطابق اللي الأدمن شافه وهو بيبني. التابات المتعلّمة
  // hidden في layout.tabs متتفلترش برا هنا
  const tabs = useMemo(() => {
    const groups = {};
    (meta?.fields || []).forEach((f) => {
      const key = f.tab || 'General';
      if (!groups[key]) groups[key] = [];
      groups[key].push(f);
    });
    return groups;
  }, [meta]);

  const tabNames = useMemo(
    () => Object.keys(tabs).filter((name) => !meta?.layout?.tabs?.[name]?.hidden),
    [tabs, meta]
  );

  useEffect(() => {
    if (tabNames.length > 0 && !tabNames.includes(activeTab)) setActiveTab(tabNames[0]);
  }, [tabNames, activeTab]);

  const sections = useMemo(() => {
    const groups = {};
    (tabs[activeTab] || []).forEach((f) => {
      const key = f.section || '';
      if (!groups[key]) groups[key] = [];
      groups[key].push(f);
    });
    return groups;
  }, [tabs, activeTab]);

  // 🎯 هيدر الشاشة بنفس منطق الشاشات الأصلية (WorkOrderDetails.js): اسم
  // التطبيق + قيمة الحقل الـ primary، وجنبها الـ description - field_name
  // بتتولّد أوتوماتيك (زي text_2) فالمطابقة لازم تكون على الـ label اللي
  // الأدمن كاتبه فعلاً مش على field_name
  const primaryField = meta?.fields?.find((f) => f.is_primary);
  const descriptionField = meta?.fields?.find((f) => f.label.trim().toLowerCase() === 'description');
  const primaryInHeader = Boolean(primaryField && HEADER_EDITABLE_TYPES.includes(primaryField.field_type));
  const descriptionInHeader = Boolean(descriptionField && HEADER_EDITABLE_TYPES.includes(descriptionField.field_type));

  const fieldLabel = (field) => field.translations?.[i18n.language] || t(`custom.field.${field.field_name}`, field.label);
  const modelLabel = meta?.translations?.[i18n.language] || t(`custom.app.${slug}`, meta?.label || slug);

  if (loading) {
    return <div className="flex items-center justify-center h-full text-gray-400"><Loader2 size={22} className="animate-spin" /></div>;
  }
  if (!meta) return null;

  const Icon = resolveIcon(meta.icon_name);
  // 🎯 "Show title bar" دلوقتي مفتاح واحد شامل لبلوك العنوان كله (اسم
  // التطبيق + برايمري + دسكريبشن) - قبل كده كان بيستثني البرايمري فضل
  // ظاهر حتى لو الخيار متقفل، فكان بيبان وكأنه متجاهل الاختيار
  const showTitleBlock = meta.layout?.show_title !== false;

  const setHeaderField = (field, rawValue) => {
    const v = field.field_type === 'number' ? (rawValue === '' ? null : Number(rawValue)) : rawValue;
    updateData((prev) => ({ ...prev, [field.field_name]: v }));
  };
  const headerInputType = (field) => (field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : field.field_type === 'datetime' ? 'datetime-local' : 'text');

  return (
    <div className="flex flex-col h-full bg-surface-app overflow-auto" style={{ fontSize: `${fontSize}px` }}>
      {/* 🎯 هيدر غامق زي شريط الأكشن الحقيقي في WorkOrderDetails.js (Save/
      Route Workflow...) - مش شريط أبيض باهت */}
      <div className="bg-primary-dark text-white p-4 shadow-card flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4 min-w-0">
          <button onClick={() => navigate(`/custom/${slug}`)} className="text-white/70 hover:text-white transition-colors shrink-0">
            <ArrowLeft size={20} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          {showTitleBlock && (
            // 🎯 برايمري ودسكربشن جنب بعض في نفس السطر (زي "WORK ORDER
            // WO4778  DESCRIPTION: ...") مش سطرين فوق بعض
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <Icon size={16} className="text-white/80 shrink-0" />
              <span className="font-black italic uppercase text-sm tracking-tighter truncate shrink-0">{modelLabel}</span>
              {primaryField && (
                primaryInHeader ? (
                  <input
                    form="custom-record-form"
                    type={headerInputType(primaryField)}
                    required={primaryField.is_required}
                    disabled={!isNew || primaryField.is_readonly}
                    title={!isNew ? t('customApps.primaryLocked', 'Locked after the record is saved') : ''}
                    value={data[primaryField.field_name] ?? ''}
                    placeholder={fieldLabel(primaryField)}
                    onChange={(e) => setHeaderField(primaryField, e.target.value)}
                    onBlur={() => handleFieldBlur(primaryField)}
                    className="bg-transparent outline-none border-b-2 border-white/0 focus:border-white text-white font-black italic uppercase text-sm tracking-tighter min-w-[70px] max-w-[220px] placeholder:text-white/50 shrink-0 disabled:cursor-not-allowed disabled:opacity-90"
                  />
                ) : (data[primaryField.field_name] && <span className="font-black italic uppercase text-sm tracking-tighter truncate shrink-0">{data[primaryField.field_name]}</span>)
              )}
              {statusEnabled && currentStatus && (
                <span className="bg-status-warning/20 text-status-warning border border-status-warning/40 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wide shrink-0">
                  {currentStatus}
                </span>
              )}
              {descriptionField && (
                descriptionInHeader ? (
                  <div className="flex items-center gap-1 min-w-0 ps-2 border-s border-white/20">
                    <span className="text-[9px] font-bold text-white/60 uppercase tracking-wide shrink-0">{fieldLabel(descriptionField)}:</span>
                    <input
                      form="custom-record-form"
                      type={headerInputType(descriptionField)}
                      required={descriptionField.is_required}
                      disabled={descriptionField.is_readonly}
                      value={data[descriptionField.field_name] ?? ''}
                      onChange={(e) => setHeaderField(descriptionField, e.target.value)}
                      onBlur={() => handleFieldBlur(descriptionField)}
                      style={{ width: descriptionField.col_span ? `${descriptionField.col_span * 200}px` : '280px' }}
                      className="bg-transparent outline-none border-b border-white/0 focus:border-white text-[10px] font-bold text-white/90 uppercase tracking-wide min-w-[100px]"
                    />
                  </div>
                ) : (data[descriptionField.field_name] && (
                  <span className="text-[9px] font-bold text-white/60 uppercase tracking-wide truncate ps-2 border-s border-white/20">
                    {fieldLabel(descriptionField)}: {data[descriptionField.field_name]}
                  </span>
                ))
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {!isNew && nav.total > 0 && (
            // 🎯 نافيجيشن جري/تالي زي شاشة الورك اوردر بالظبط
            <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
              <button disabled={!nav.prev} onClick={() => goToRecord(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} /></button>
              <button disabled={!nav.prev} onClick={() => goToRecord(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} /></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
                <span className="text-status-warning">{nav.current_pos}</span> / {nav.total}
              </div>
              <button disabled={!nav.next} onClick={() => goToRecord(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} /></button>
              <button disabled={!nav.next} onClick={() => goToRecord(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} /></button>
            </div>
          )}
          {statusEnabled && meta.layout?.status?.domain_id && !isNew && (
            <button onClick={() => setShowStatusModal(true)} className="p-2 hover:bg-white/10 rounded text-white/70 transition-colors" title={t('common.change_status', 'Change Status')}>
              <Tag size={18} />
            </button>
          )}
          {meta.accepts_reports && !isNew && (
            <SmartPrintButton moduleName={`CUSTOM_${slug.toUpperCase()}`} refId={id} />
          )}
          {meta.accepts_attachments && (
            <button
              onClick={() => {
                if (isNew) { toast.error(t('customApps.saveFirstForAttachments', 'Save the record first, then you can attach files')); return; }
                setShowAttachments(true);
              }}
              className={`relative transition-colors ${isNew ? 'text-white/30 cursor-not-allowed' : 'text-white/80 hover:text-white'}`}
              title={isNew ? t('customApps.saveFirstForAttachments', 'Save the record first, then you can attach files') : t('common.attachments', 'Attachments')}
            >
              <Paperclip size={18} />
              {attachmentCount > 0 && <span className="absolute -top-1.5 -end-1.5 bg-status-danger text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{attachmentCount}</span>}
            </button>
          )}
          <button
            type="submit"
            form="custom-record-form"
            disabled={saving}
            className="bg-white text-primary-dark px-6 py-2 rounded-control font-black flex items-center gap-2 uppercase text-[11px] hover:bg-white/90 transition-all shadow-lg active:scale-95 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {t('common.save', 'Save')}
          </button>
          {!isNew && (
            <button onClick={() => setShowAudit(true)} className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded transition-colors" title={t('sidebar.audit_log', 'Audit Log')}>
              <History size={18} />
            </button>
          )}
          {!isNew && canDelete && (
            <button onClick={doDelete} className="p-2 text-white/80 hover:text-status-danger hover:bg-white/10 rounded transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {meta.accepts_attachments && (
        <AttachmentsModal isOpen={showAttachments} onClose={() => setShowAttachments(false)} ownertable={`custom_${slug}`} ownerid={id} />
      )}
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName={`custom_record:${slug}`} recordId={id} />
      {showStatusModal && (
        <StatusModal
          currentStatus={currentStatus}
          recordId={id}
          appName={statusAppName}
          domainId={meta.layout?.status?.domain_id || ''}
          onClose={() => setShowStatusModal(false)}
          onComplete={load}
        />
      )}
      {showStatusHistory && (
        <StatusHistoryModal appName={statusAppName} recordId={id} onClose={() => setShowStatusHistory(false)} />
      )}

      {tabNames.length > 1 && (
        <div className="flex bg-surface-card border-b border-line-subtle px-4 overflow-x-auto no-scrollbar">
          {tabNames.map((tabName) => {
            const TabIcon = resolveIcon(meta.layout?.tabs?.[tabName]?.icon, null);
            const isActive = activeTab === tabName;
            const tabLabel = meta.layout?.tabs?.[tabName]?.translations?.[i18n.language] || tabName;
            return (
              <button
                key={tabName}
                onClick={() => setActiveTab(tabName)}
                className={`flex items-center gap-1.5 px-6 py-2.5 whitespace-nowrap text-[10px] font-black uppercase tracking-tighter transition-all ${isActive ? 'border-b-2 border-primary-dark text-primary-dark' : 'text-ink-secondary hover:text-ink'}`}
              >
                {TabIcon && <TabIcon size={13} className={isActive ? 'text-primary-dark' : 'text-ink-secondary'} />}
                {tabLabel}
              </button>
            );
          })}
        </div>
      )}

      <form id="custom-record-form" onSubmit={handleSubmit} className="p-6 max-w-6xl w-full mx-auto space-y-6">
        {Object.entries(sections).map(([section, sectionFields]) => {
          const sectionLayout = meta.layout?.sections?.[`${activeTab}::${section || ''}`];
          const columns = sectionLayout?.columns || 2;
          const sectionLabel = sectionLayout?.translations?.[i18n.language] || section;
          // الحقول اللي بقت قابلة للكتابة في الهيدر (برايمري/دسكريبشن)
          // مبتتكررش تاني هنا
          const visibleFields = sectionFields.filter((f) => {
            if (primaryInHeader && f.id === primaryField.id) return false;
            if (descriptionInHeader && f.id === descriptionField.id) return false;
            return true;
          });
          if (visibleFields.length === 0) return null;
          // 🎯 نفس ستايل الـ Section الحقيقي في WorkOrderDetails.js بالظبط:
          // كارت بحد خفيف + هيدر رمادي فاتح مع نقطة/شرطة خضرا صغيرة قبل
          // العنوان - مش عنوان عادي معلّق فوق الحقول
          const fieldsGrid = (
            <div className="grid gap-x-6" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
              {visibleFields.map((field) => {
                const isBoxType = field.field_type === 'table' || field.field_type === 'image';
                // 🎯 col_span بيدّي تحكم صريح في عدد أعمدة السكشن اللي الحقل
                // بياخدها - لو مش متحدد، نرجع للسلوك القديم (فُل عرض
                // لل table/image، عمود واحد للباقي) عشان أي تطبيق قديم
                // ميتأثرش
                const spanStyle = field.col_span
                  ? { gridColumn: `span ${Math.min(field.col_span, columns)}` }
                  : undefined;
                return (
                  <div
                    key={field.id}
                    onBlur={() => handleFieldBlur(field)}
                    style={spanStyle}
                    className={`flex flex-col gap-1 px-1 py-1.5 rounded-sm transition-all ${isBoxType && !field.col_span ? 'md:col-span-full' : ''} ${isBoxType ? '' : 'border-b border-line-subtle group hover:bg-primary-soft focus-within:border-primary'}`}
                  >
                    <label className={`${field.is_required ? 'text-status-danger' : 'text-ink-secondary'} font-bold text-[0.8] uppercase tracking-tighter`}>
                      {fieldLabel(field)}
                    </label>
                    <FieldInput
                      field={field}
                      value={data[field.field_name]}
                      onChange={(v) => updateData((prev) => ({ ...prev, [field.field_name]: v }))}
                      disabled={field.is_readonly}
                    />
                  </div>
                );
              })}
            </div>
          );
          if (!section) {
            return <div key="default">{fieldsGrid}</div>;
          }
          return (
            <div key={section} className="bg-surface-card border border-line rounded shadow-sm">
              <div className="p-2 bg-surface-panel border-b border-line font-bold text-ink-secondary uppercase text-[0.8] tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-3 bg-primary rounded-full" />
                {sectionLabel}
              </div>
              <div className="p-5">{fieldsGrid}</div>
            </div>
          );
        })}
      </form>
    </div>
  );
};

export default CustomModelForm;
